import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, get, patch, post, postForm, withQuery } from "./client";

/** The delivery screens' APIs: ingest, extraction, red list. The dashboard
 * and workflow have their own modules (dashboard.ts, workflow.ts). */

// --- ingest ----------------------------------------------------------------

export interface IngestContext {
  contracts: { id: number; code: string; name: string }[];
  regions: { id: number; name: string; contract_id: number }[];
  clusters: { id: number; name: string; region_id: number | null; contract_id: number }[];
  history: {
    batch_id: number;
    cluster_name: string | null;
    uploaded_at: string;
    uploaded_by_email: string | null;
    committed_at: string | null;
    state: string;
    file_count: number;
    loaded: number;
    not_loaded: number;
    with_issues: number;
  }[];
}

export interface StagedRow {
  row_id: number;
  unreadable: boolean;
  filename: string;
  extracted: Record<string, unknown>;
  action: string;
  issues: { code: string; detail: string }[];
  location: { lat: number | null; lon: number | null; source: string | null; label: string; confidence: string | null };
}

export interface Batch {
  batch: { id: number; contract_id: number; state: string; file_count: number };
  rows: StagedRow[];
  summary?: unknown;
  report_csv_url?: string;
}

export interface Progress {
  token: string;
  phase: string;
  phase_label: string;
  steps: string[];
  step_labels: Record<string, string>;
  files_total: number;
  files_done: number;
  files_failed: number;
  finished: boolean;
  error: string | null;
  files: { filename: string; step?: string; done?: boolean; failed?: boolean; detail?: string }[];
}

export function useIngestContext() {
  return useQuery<IngestContext, ApiError>({
    queryKey: ["ingest", "context"],
    queryFn: () => get<IngestContext>("/api/v1/ingest"),
  });
}

export function useBatch(id: number | null) {
  return useQuery<Batch, ApiError>({
    queryKey: ["ingest", "batch", id],
    queryFn: () => get<Batch>(`/api/v1/ingest/${id}`),
    enabled: id !== null,
  });
}

export function useProgress(token: string | null) {
  return useQuery<Progress, ApiError>({
    queryKey: ["ingest", "progress", token],
    queryFn: () => get<Progress>(`/api/v1/ingest/progress/${token}`),
    enabled: token !== null,
    refetchInterval: (query) => (query.state.data?.finished ? false : 800),
    retry: 3,
  });
}

export function useUploadBatch() {
  const client = useQueryClient();
  return useMutation<Batch, ApiError, { contractId: number; files: File[]; token: string }>({
    mutationFn: ({ contractId, files, token }) => {
      const form = new FormData();
      form.append("contract_id", String(contractId));
      form.append("progress_token", token);
      for (const file of files) {
        form.append("files", file, file.name);
      }
      return postForm<Batch>("/api/v1/ingest", form);
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["ingest"] });
      void client.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

export function useCommitBatch(batchId: number) {
  const client = useQueryClient();
  return useMutation<{ result: Record<string, number>; summary: unknown; history: IngestContext["history"] }, ApiError, number[]>({
    mutationFn: (rowIds) => post(`/api/v1/ingest/${batchId}/commit`, { row_ids: rowIds }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["ingest"] });
      void client.invalidateQueries({ queryKey: ["sites"] });
      void client.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

export function useWorkbookImport(path: "schedule" | "approved-rates" | "spares-catalogue") {
  const client = useQueryClient();
  return useMutation<{ report: Record<string, unknown> }, ApiError, { contractId?: number; file: File }>({
    mutationFn: ({ contractId, file }) => {
      const form = new FormData();
      if (contractId !== undefined) {
        form.append("contract_id", String(contractId));
      }
      form.append("file", file, file.name);
      return postForm<{ report: Record<string, unknown> }>(`/api/v1/ingest/${path}`, form);
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["sites"] });
    },
  });
}

// --- extraction (existing /api/qcp endpoints) --------------------------------

export interface Extraction {
  id: number;
  [key: string]: unknown;
}

export function useExtractionQueue(batchId: string) {
  return useQuery<{ count: number; reviewBelow: number; extractions: Extraction[] }, ApiError>({
    queryKey: ["extractions", batchId],
    queryFn: () => get(withQuery("/api/qcp/extractions", { batch_id: batchId })),
  });
}

export function useExtraction(id: number | null) {
  return useQuery<Extraction, ApiError>({
    queryKey: ["extractions", "row", id],
    queryFn: () => get<Extraction>(`/api/qcp/extractions/${id}`),
    enabled: id !== null,
  });
}

export function useExtractionHistory(id: number | null) {
  return useQuery<{ id: number; edits: { field: string; from: unknown; to: unknown; at: string; by: string | null }[] }, ApiError>({
    queryKey: ["extractions", "history", id],
    queryFn: () => get(`/api/qcp/extractions/${id}/history`),
    enabled: id !== null,
  });
}

export function useCorrectExtraction(id: number) {
  const client = useQueryClient();
  return useMutation<{ corrected: string[]; extraction: Extraction }, ApiError, Record<string, unknown>>({
    mutationFn: (body) => patch(`/api/qcp/extractions/${id}`, body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["extractions"] });
    },
  });
}

// --- red list --------------------------------------------------------------

export interface RedListContext {
  contracts: { id: number; code: string; name: string }[];
  uploads: { source: string; filename: string; uploaded_at: string | null; sites: number; earliest: string | null; latest: string | null }[];
  template_url: string;
  report?: Record<string, unknown>;
  removed?: number;
}

export function useRedList() {
  return useQuery<RedListContext, ApiError>({
    queryKey: ["redlist"],
    queryFn: () => get<RedListContext>("/api/v1/redlist"),
  });
}

function useRedListWrite<TVars>(write: (vars: TVars) => Promise<RedListContext>) {
  const client = useQueryClient();
  return useMutation<RedListContext, ApiError, TVars>({
    mutationFn: write,
    onSuccess: (context) => {
      client.setQueryData(["redlist"], context);
      void client.invalidateQueries({ queryKey: ["sites"] });
    },
  });
}

export function useUploadRedList() {
  return useRedListWrite(({ contractId, file }: { contractId: number; file: File }) => {
    const form = new FormData();
    form.append("contract_id", String(contractId));
    form.append("file", file, file.name);
    return postForm<RedListContext>("/api/v1/redlist", form);
  });
}

export function useRemoveRedListUpload() {
  return useRedListWrite((source: string) => post<RedListContext>("/api/v1/redlist/uploads/remove", { source }));
}

export function useClearRedList() {
  return useRedListWrite(() => post<RedListContext>("/api/v1/redlist/uploads/clear"));
}
